import { defaultMonthlyData, DISTRIBUTION_TYPES } from '../constants/spend.constants';
import {
  DistributionFieldType,
  IBudget,
  IForecastBudget,
  ILineItem,
  IMonthlySpendData,
  ISubitem,
} from '@app/store/spend/spend.interfaces';
import { DeepCopyService } from '@app/services/deep-copy.service';

export function updateLineItemWithSubitem(
  parentItem: ILineItem,
  updatedSubitem: Partial<ISubitem>,
) {
  const newParentItem = {
    ...parentItem,
    distribution: DISTRIBUTION_TYPES.MANUAL,
    subitems: parentItem.subitems.map((subitem) => {
      if (subitem.id === updatedSubitem.id) {
        return {
          ...subitem,
          ...updatedSubitem,
        };
      }
      return subitem;
    }),
  };

  // we don't need to update the parent budget and dates based on subitems as the budget is calculated dinamically
  // left this here if we might need it 👀
  // starts off empty, then filled with total yearly budgets based on subitems
  // let parentBudget: IBudget[] = [];
  // let startDate = null;
  // let endDate = null;
  //
  // for (const subitem of newParentItem.subitems) {
  //   if (!startDate || subitem.start_date < startDate) {
  //     startDate = subitem.start_date;
  //   }
  //   const subitemEndDate = dayjs(subitem.start_date, 'YYYY-MM-DD')
  //     .add(subitem.duration, 'month')
  //     .format('YYYY-MM-DD');
  //   if (!endDate || subitemEndDate > endDate) {
  //     endDate = subitemEndDate;
  //   }
  //
  //   for (const yearlySubItemBudget of subitem.budget) {
  //     parentBudget = updateBudgetWithSubitemBudget(yearlySubItemBudget, parentBudget);
  //   }
  // }
  //
  // const end = dayjs(endDate, 'YYYY-MM-DD');
  // const start = dayjs(startDate, 'YYYY-MM-DD');
  // newParentItem.budget = parentBudget;
  // newParentItem.start_date = startDate;
  // newParentItem.duration = end.year() * 12 + end.month() - (start.year() * 12 + start.month()) + 1;

  return newParentItem;
}

/**
 * Based on one year's subitem budget, updates the overall parent budget.
 * If a year is present in the subitem, but not in the parent budget, it adds a new year.
 * Otherwise, it updates the monthly budget values for that year.
 * Returns the overall parent budget with the updated values.
 * @param subitemBudget
 * @param parentBudget
 */
export function updateBudgetWithSubitemBudget(subitemBudget: IBudget, parentBudget: IBudget[]) {
  const year = subitemBudget.year;
  const yearlyParentBudget = DeepCopyService.deepCopy(parentBudget.find((el) => el.year === year));
  const hasMonthlyPrime = !!subitemBudget.monthly_prime;

  if (!yearlyParentBudget) {
    const newYearlyBudget: IBudget = {
      year,
      monthly_budget: { ...subitemBudget.monthly_budget },
    };
    if (hasMonthlyPrime) {
      newYearlyBudget.monthly_prime = { ...subitemBudget.monthly_prime };
    }

    parentBudget.push(newYearlyBudget);
    return parentBudget;
  }

  for (const [month, budgetValue] of Object.entries(yearlyParentBudget.monthly_budget)) {
    yearlyParentBudget.monthly_budget[month] =
      budgetValue + (subitemBudget.monthly_budget?.[month] ?? 0);
  }

  if (hasMonthlyPrime) {
    for (const [month, primeValue] of Object.entries(yearlyParentBudget.monthly_prime)) {
      yearlyParentBudget.monthly_prime[month] =
        primeValue + (subitemBudget.monthly_prime?.[month] ?? 0);
    }
  }

  return parentBudget.map((yearlyBudget) => {
    if (yearlyBudget.year === year) {
      return yearlyParentBudget;
    }
    return yearlyBudget;
  });
}

export const getProjectTotalPerItem = (
  budget: IBudget[] | IForecastBudget[],
  budgetKey: DistributionFieldType = 'monthly_budget',
) => {
  if (!budget || budget.length === 0) {
    return 0;
  }
  return budget
    .map((bud: IBudget | IForecastBudget) => bud?.[budgetKey] ?? { ...defaultMonthlyData })
    .reduce(
      (projectTotal, monthlySpendData) =>
        projectTotal +
        Object.values(monthlySpendData).reduce(
          (yearlyTotal: number, monthlyValue: number) => yearlyTotal + monthlyValue,
          0,
        ),
      0,
    );
};

export const getYearlyTotalPerItem = (monthlyData: IMonthlySpendData) => {
  return monthlyData ? Object.values(monthlyData).reduce((acc, curr) => acc + curr, 0) : 0;
};

export const getMonthlyDataForYear = (
  budget: IBudget[],
  selectedYear: number,
  budgetKey = 'monthly_budget',
): IMonthlySpendData => {
  const yearData = budget?.find((bud) => bud.year === selectedYear);
  return yearData?.[budgetKey] ?? { ...defaultMonthlyData };
};

export const getMonthlyForecastForYear = (
  budget: IForecastBudget[],
  selectedYear: number,
): IMonthlySpendData => {
  const yearData = budget?.find((bud) => bud.year === selectedYear);
  return yearData?.monthly_forecast ?? { ...defaultMonthlyData };
};
